import { createContext, useContext } from "react";
import { validToken, YStack } from "@medbillai/ui";
import { BackgroundRadialGrantedGradient } from "../BackgroundRadialGrantedGradient";
import { ContentBody, FooterStrip, type OnboardingContentProps, OnboardingDisclaimer, type OnboardingFooterProps, OnboardingFormError, OnboardingHeader, OnboardingNav, OnboardingPrimaryAction, type OnboardingScreenProps, OnboardingSecondaryAction, OnboardingTertiaryAction, OnboardingToneContext, TONE_BACKGROUND, TONE_BLEED_GUARD } from "./OnboardingScreen.shared";

// Web mechanics: the screen behaves like a normal scrolling web page, not a
// height-capped app shell. At phone widths the root is *at least* the
// visible viewport tall (min-height:100dvh) and grows with its content — so
// when a resize-model browser (Chrome iOS) shrinks the web view for its
// keyboard, nothing above the footer reflows; the page just becomes taller
// than the view and the browser reveals the focused input by ordinary page
// scrolling. A capped height:100dvh variant instead reflows the whole
// screen at keyboard-open. Above $gtSm the desktop hero shell stays capped
// and content scrolls internally. At phone widths, the 100dvh floor keeps the
// footer at the visible bottom while the page remains free to grow with content.

// Compound-component wiring for the `pinnedFooter` prop: Content and Footer
// are the caller's own elements, so Root can only reach them via context.
// Private to this file — callers use the prop.
const PinnedFooterContext = createContext(true);

/**
 * Content region. At phone widths the page is the scroll surface and, when
 * pinned, this region absorbs spare viewport height so the footer lands on
 * the visible bottom; above $gtSm it scrolls internally instead.
 */
function OnboardingContent({
  children
}: OnboardingContentProps) {
  const pinnedFooter = useContext(PinnedFooterContext);
  return <YStack width="100%" maxWidth={456} alignSelf="center" flexGrow={pinnedFooter ? 1 : 0} $gtSm={{
    flexGrow: 0,
    maxHeight: "100%",
    overflowY: "auto"
  }} flexShrink={1} flexBasis="auto" minHeight={0} data-sentry-element="YStack" data-sentry-component="OnboardingContent" data-sentry-source-file="OnboardingScreen.tsx">
      <ContentBody data-sentry-element="ContentBody" data-sentry-source-file="OnboardingScreen.tsx">{children}</ContentBody>
    </YStack>;
}
function OnboardingFooter({
  children,
  isFading
}: OnboardingFooterProps) {
  const pinnedFooter = useContext(PinnedFooterContext);
  return <YStack width="100%" maxWidth={456} alignSelf="center"
  // Pinned footers sit on the page's bottom edge — keep a gap above the
  // browser chrome / home indicator. (Unpinned and desktop footers hug
  // content and already have space.)
  paddingBottom={pinnedFooter ? "$sp2" : undefined} $gtSm={{
    paddingBottom: 0
  }} data-sentry-element="YStack" data-sentry-component="OnboardingFooter" data-sentry-source-file="OnboardingScreen.tsx">
      <FooterStrip isFading={isFading} data-sentry-element="FooterStrip" data-sentry-source-file="OnboardingScreen.tsx">{children}</FooterStrip>
    </YStack>;
}
function OnboardingScreenRoot({
  showBackgroundGradient,
  pinnedFooter = true,
  contained = false,
  tone = "light",
  children
}: OnboardingScreenProps) {
  return <OnboardingToneContext.Provider value={tone} data-sentry-element="OnboardingToneContext.Provider" data-sentry-component="OnboardingScreenRoot" data-sentry-source-file="OnboardingScreen.tsx">
      <PinnedFooterContext.Provider value={pinnedFooter} data-sentry-element="PinnedFooterContext.Provider" data-sentry-source-file="OnboardingScreen.tsx">
        <YStack flex={1} backgroundColor={TONE_BACKGROUND[tone]}
      // Covers the sub-pixel clip seam at the right edge.
      boxShadow={TONE_BLEED_GUARD[tone]}
      // Content-sized so height propagates through the auto-height
      // transition wrappers at phone widths.
      flexBasis="auto"
      // Floor, not a cap: content absorbs spare height and the footer lands
      // on the visible bottom. CSS-only so SSR'd desktop never carries it.
      minHeight={pinnedFooter && !contained ? validToken("100dvh") : undefined}
      // A definite height instead, so a screen's own internal scroll
      // region can shrink to the space between header and footer.
      height={contained ? validToken("100dvh") : undefined}
      // Zero, not `auto`: `min-height: auto` would floor the root at its own
      // content height and grow past the desktop shell's cap.
      $gtSm={{
        minHeight: 0,
        height: "auto"
      }} data-sentry-element="YStack" data-sentry-source-file="OnboardingScreen.tsx">
          {showBackgroundGradient ? <BackgroundRadialGrantedGradient fullscreen /> : null}
          {children}
        </YStack>
      </PinnedFooterContext.Provider>
    </OnboardingToneContext.Provider>;
}
export const OnboardingScreen = Object.assign(OnboardingScreenRoot, {
  Nav: OnboardingNav,
  Header: OnboardingHeader,
  Content: OnboardingContent,
  Footer: OnboardingFooter,
  FormError: OnboardingFormError,
  PrimaryAction: OnboardingPrimaryAction,
  SecondaryAction: OnboardingSecondaryAction,
  TertiaryAction: OnboardingTertiaryAction,
  Disclaimer: OnboardingDisclaimer
});
export type { OnboardingHeaderProps, OnboardingNavProps, OnboardingPrimaryActionProps, OnboardingScreenProps } from "./OnboardingScreen.shared";
export { computeOpenedOffset, partitionFooterChildren } from "./OnboardingScreen.shared";